import axios from 'axios';

const API_URL = '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

console.log('API URL:', API_URL);

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Handle 401 Unauthorized - redirect to login
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/password', { currentPassword, newPassword })
};

// Users API (admin only)
export const usersAPI = {
  getAll: (filters = {}) => api.get('/users', { params: filters }),
  getById: (id) => api.get(`/users/${id}`),
  create: (userData) => api.post('/users', userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  resetPassword: (id, password) => api.put(`/users/${id}/password`, { password }),
  delete: (id) => api.delete(`/users/${id}`)
};

// Banks API (admin only)
export const banksAPI = {
  getAll: () => api.get('/banks'),
  getById: (id) => api.get(`/banks/${id}`),
  create: (bankData) => api.post('/banks', bankData),
  update: (id, bankData) => api.put(`/banks/${id}`, bankData),
  delete: (id, force = false) => api.delete(`/banks/${id}${force ? '?force=true' : ''}`)
};

// RFCs API (admin only)
export const rfcsAPI = {
  getAll: () => api.get('/rfcs'),
  getById: (id) => api.get(`/rfcs/${id}`),
  create: (rfcData) => api.post('/rfcs', rfcData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, rfcData) => api.put(`/rfcs/${id}`, rfcData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id, force = false) => api.delete(`/rfcs/${id}${force ? '?force=true' : ''}`)
};

// Webs API
export const websAPI = {
  getAll: () => api.get('/webs'),
  getActive: () => api.get('/webs/active'),
  getById: (id) => api.get(`/webs/${id}`),
  create: (webData) => api.post('/webs', webData),
  update: (id, webData) => api.put(`/webs/${id}`, webData),
  delete: (id, force = false) => api.delete(`/webs/${id}${force ? '?force=true' : ''}`)
};

// Templates API (admin only)
export const templatesAPI = {
  get: () => api.get('/templates'),
  update: (templateData) => api.put('/templates', templateData),
  uploadLogo: (formData) => api.post('/templates/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: () => api.delete('/templates/logo'),
  uploadSatImage: (formData) => api.post('/templates/sat-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Quotes API
export const quotesAPI = {
  getAll: (filters = {}) => api.get('/quotes', { params: filters }),
  getById: (id) => api.get(`/quotes/${id}`),
  create: (quoteData) => api.post('/quotes', quoteData),
  update: (id, quoteData) => api.put(`/quotes/${id}`, quoteData),
  getPDF: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
  duplicate: (id) => api.post(`/quotes/${id}/duplicate`),
  delete: (id) => api.delete(`/quotes/${id}`),
  searchWeb: (query) => api.get('/quotes/search-web', { params: { q: query } }),
  getStats: () => api.get('/quotes/stats')
};

// Groups API
export const groupsAPI = {
  getAll: (filters = {}) => api.get('/groups', { params: filters }),
  getById: (id) => api.get(`/groups/${id}`),
  create: (groupData) => api.post('/groups', groupData),
  update: (id, groupData) => api.put(`/groups/${id}`, groupData),
  delete: (id) => api.delete(`/groups/${id}`),
  search: (query, webId) => api.get('/groups/search', { params: { q: query, web_id: webId } })
};

// Google Sheets API
export const googleAPI = {
  getStatus: () => api.get('/google/status'),
  getAuthUrl: () => api.get('/google/auth'),
  connect: (code) => api.post('/google/connect', { code }),
  disconnect: () => api.post('/google/disconnect'),
  previewSheet: (url, range) => api.post('/google/sheets/preview', { url, range })
};

// Import API
export const importAPI = {
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  parse: (file, mapping) => {
    const formData = new FormData();
    formData.append('file', file);
    Object.keys(mapping).forEach(key => {
      formData.append(key, mapping[key]);
    });
    return api.post('/import/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadTemplate: () => api.get('/import/template', { responseType: 'blob' })
};

// Stats API (admin only)
export const statsAPI = {
  getDashboard: (filters = {}) => api.get('/stats/dashboard', { params: filters }),
  getQuotesByWeb: (filters = {}) => api.get('/stats/quotes-by-web', { params: filters }),
  getQuotesByUser: (filters = {}) => api.get('/stats/quotes-by-user', { params: filters }),
  getWebsWithoutQuotes: () => api.get('/stats/webs-without-quotes')
};

export default api;
